import React, { useMemo } from 'react';
import type { TableColumnDef } from '../../../shared/table/types.ts';
import { AppDataTable } from '../../../shared/table/AppDataTable.tsx';
import { useScopeOverdueSummary } from '../../../hooks/analytics/useScopeReports.ts';
import { formatDate, RefreshMeta, SectionHeader } from '../controller/shared.tsx';
import type { ScopeOverduePbcRow, ScopeOverdueTestingRow } from '../../../request/analytics/scopeReports.ts';

/**
 * Drill-through detail behind the CAE dashboard's overdue testing/PBC KPI
 * tiles (`ScopeOverdueCard`) — the two capped-50 row lists from
 * `GET /api/analytics/scope/overdue-summary`, rendered as their own "Overdue"
 * tab rather than crammed into the KPI tile itself.
 */
export const ScopeOverdueDetail: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useScopeOverdueSummary();
  const testingRows = useMemo(() => data?.testing ?? [], [data]);
  const pbcRows = useMemo(() => data?.pbc ?? [], [data]);

  const testingColumns = useMemo<TableColumnDef<ScopeOverdueTestingRow>[]>(
    () => [
      { id: 'control_title', columnLabel: 'Control', header: () => 'Control', cell: ({ row }) => row.original.control_title, widthPercent: 30 },
      { id: 'assignee_name', columnLabel: 'Assignee', header: () => 'Assignee', cell: ({ row }) => row.original.assignee_name ?? 'Unassigned', widthPercent: 20 },
      { id: 'due_date', columnLabel: 'Due', header: () => 'Due', cell: ({ row }) => formatDate(row.original.due_date), widthPercent: 15 },
      {
        id: 'days_overdue',
        columnLabel: 'Days Overdue',
        header: () => 'Days Overdue',
        cell: ({ row }) => (
          <span className="inline-flex items-center rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-semibold text-rose-700">
            {row.original.days_overdue}d
          </span>
        ),
        widthPercent: 15,
      },
    ],
    []
  );

  const pbcColumns = useMemo<TableColumnDef<ScopeOverduePbcRow>[]>(
    () => [
      { id: 'title', columnLabel: 'PBC Request', header: () => 'PBC Request', cell: ({ row }) => row.original.title, widthPercent: 30 },
      { id: 'assigned_to_name', columnLabel: 'Assignee', header: () => 'Assignee', cell: ({ row }) => row.original.assigned_to_name ?? 'Unassigned', widthPercent: 20 },
      { id: 'due_date', columnLabel: 'Due', header: () => 'Due', cell: ({ row }) => formatDate(row.original.due_date), widthPercent: 15 },
      {
        id: 'days_overdue',
        columnLabel: 'Days Overdue',
        header: () => 'Days Overdue',
        cell: ({ row }) => (
          // Urgency-color fix (feedback round 2026-08-23): overdue is always
          // red, matching the testing days-overdue badges above — amber was
          // inconsistent with the same "overdue" concept.
          <span className="inline-flex items-center rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-semibold text-rose-700">
            {row.original.days_overdue}d
          </span>
        ),
        widthPercent: 15,
      },
    ],
    []
  );

  return (
    <div className="grid grid-cols-1 gap-6">
      <section>
        <div className="mb-2 flex items-center justify-between">
          <SectionHeader title="Overdue Testing" description="Past-due test runs, most-overdue first" />
          <RefreshMeta updatedAt={dataUpdatedAt} onRefresh={refetch} isFetching={isFetching} />
        </div>
        <AppDataTable<ScopeOverdueTestingRow>
          data={testingRows}
          columns={testingColumns}
          loading={isLoading}
          error={isError ? error?.message ?? 'Failed to load overdue testing.' : null}
          emptyMessage="No overdue test runs."
          searchable
          paginated
          defaultPageSize={10}
        />
      </section>
      <section>
        <SectionHeader title="Overdue PBC Requests" description="Past-due evidence requests, most-overdue first" />
        <AppDataTable<ScopeOverduePbcRow>
          data={pbcRows}
          columns={pbcColumns}
          loading={isLoading}
          error={isError ? error?.message ?? 'Failed to load overdue PBC requests.' : null}
          emptyMessage="No overdue PBC requests."
          searchable
          paginated
          defaultPageSize={10}
        />
      </section>
    </div>
  );
};

export default ScopeOverdueDetail;
